import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { AuthService } from '../global-services/auth.service';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { OBJECT_STORES } from '../index-db';
import { IndexedDbService } from '../global-services/indexed-db.service';

@Injectable()
export class LoggedInGuard implements CanActivate {
    // store access log to redirect to last access url
    static lastAccessedPage: string = '';
    static previousPage: string = '';

    constructor(
        private router: Router,
        private authService: AuthService,
        private indexDBService: IndexedDbService
    ) {}

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean> | boolean {
        // Ignore url if redirected to login
        if (state.url.indexOf('login') === -1) {
            LoggedInGuard.lastAccessedPage = state.url;
        }

        const cachedCards$ = this.indexDBService
            .count(OBJECT_STORES.CARDS)
            .pipe(map((count) => count > 0));

        const authenticated$ = this.authService.isAuthenticated$.pipe(
            tap((isAuthenticated) => {
                if (!isAuthenticated) {
                    this.router.navigate(['/mobile-landing'], {
                        state: {
                            lastAccessPage: LoggedInGuard.lastAccessedPage,
                        },
                    });
                    return;
                }
            }),
            map((isAuthenticated) => {
                if (!isAuthenticated && state.url.startsWith('/sso')) {
                    return true;
                }
                return isAuthenticated;
            })
        );

        const loggedIn$ = this.authService.isLoggedIn$.pipe(
            filter((isLoggedIn) => isLoggedIn)
        );

        return cachedCards$.pipe(
            switchMap((hasCache) => {
                if (hasCache) {
                    // If cache is available, allow access without checking authentication
                    return of(true);
                }

                // If no cache, proceed with authentication checks
                return combineLatest([authenticated$, loggedIn$]).pipe(
                    // @ts-ignore
                    map(([isAuthenticated, isLoggedIn]) => {
                        return isAuthenticated && isLoggedIn;
                    })
                );
            })
        );
    }
}
